import {createBrowserRouter, createRoutesFromElements, Navigate, Route} from "react-router-dom";
import AdminPanelGuard from "./guards/AdminPanelGuard";
import PrivateRoute from "./guards/PrivateRoute";
import RegistrationFeatureGuard from "./guards/RegistrationFeatureGuard";
import FirstInstanceGuard from './guards/FirstInstanceGuard';

import FileViewer from "./components/common/FileViewer";
import Layout from "./components/layout/Layout";
import {PageTitleKeys, RoutePaths} from "./config/routeConstants";
import PrivateLayout from './layouts/PrivateLayout';
import PublicLayout from './layouts/PublicLayout';
import AdminDataSourceList from "./pages/admin/AdminDataSourceList";
import AppInfo from "./pages/admin/AppInfo";
import Logs from "./pages/admin/Logs";
import Configuration from "./pages/admin/Configuration";
import CreateUser from "./pages/admin/CreateUser";
import DataSourceDocumentList from "./pages/admin/DataSourceDocumentList";
import DataSourceUpdateScheduler from "./pages/admin/DataSourceUpdateScheduler";
import GroupDetails from "./pages/admin/GroupDetails";
import GroupList from "./pages/admin/GroupList";
import Prompts from "./pages/admin/Prompts";
import ReportedIssuesList from "./pages/admin/ReportedIssuesList";
import UserEdit from "./pages/admin/UserEdit";
import UserList from "./pages/admin/UserList";
import AccountActivation from "./pages/auth/AccountActivation";
import ChangePassword from "./pages/auth/ChangePassword";
import CheckYourMailbox from "./pages/auth/CheckYourMailbox";
import EmailConfirmed from "./pages/auth/EmailConfirmed";
import FirstPassword from "./pages/auth/FirstPassword";
import Login from "./pages/auth/Login";
import VerifyTwoFactorAuth from "./pages/auth/VerifyTwoFactorAuth";
import Register from "./pages/auth/Register";
import RequestPasswordReset from "./pages/auth/RequestPasswordReset";
import ThankYouForRegistration from "./pages/auth/ThankYouForRegistration";
import Manual from "./pages/manual/Manual";
import UserSettings from "./pages/user-settings/UserSettings";
import ChatWorkspace from "./pages/workspace/ChatWorkspace";
import PaymentFailure from "./pages/workspace/Payments/PaymentFailure";
import Payments from "./pages/workspace/Payments/Payments";
import PaymentSuccess from "./pages/workspace/Payments/PaymentSuccess";
import UserDataSourceList from "./pages/workspace/UserDataSourceList";
import RegisterAdmin from './pages/auth/RegisterAdmin';
import TextTranslatorPage from "./pages/text-translator/TextTranslatorPage";
import OcrBatch from "./pages/ocr-batch/OcrBatch";
import OcrBatchDetails from "./pages/ocr-batch/OcrBatchDetails";
import OcrResultPreview from "./pages/ocr-batch/OcrResultPreview";
import DocumentsDigitization from "./pages/documents-digitization/DocumentsDigitization";
import DigitizationDetails from "./pages/documents-digitization/DigitizationDetails";
import DigitizationEdition from "./pages/documents-digitization/DigitizationEdition";
import Statistics from "./pages/admin/Statistics";
import Dashboard from "./pages/dashboard";
import {FeatureGuard} from "./guards/FeatureGuard";
import {FEATURE_KEYS} from "./models/admin-config.models";
import EntraCallback from "./pages/auth/EntraCallback";
import AdminRouteGuard from "./guards/AdminRouteGuard";
import Eula from "./pages/auth/Eula";
import AuthenticationRequiredGuard from "./guards/AuthenticationRequiredGuard";
import ConsentRequiredGuard from "./guards/ConsentRequiredGuard";
import LicenseRequiredGuard from "./guards/LicenseRequiredGuard";
import RoleLayout from "./layouts/RoleLayout";
import PublicOnlyGuard from "./guards/PublicOnlyGuard";
import LicenseActivation from "./pages/auth/LicenseActivation";
import LicenseManagement from "./pages/admin/LicenseManagement";
import LicenseActivationGuard from "./guards/LicenseActivationGuard";
import LicenseRoleGuard from "./guards/LicenseRoleGuard";

export const router = createBrowserRouter(
    createRoutesFromElements(
        <Route path={RoutePaths.Root}>
            <Route element={<AdminRouteGuard/>}>
                <Route path={RoutePaths.RegisterAdmin} element={<RegisterAdmin/>} handle={{titleKey: PageTitleKeys.Register}}/>
            </Route>

            <Route element={<FirstInstanceGuard/>}>
                <Route path={RoutePaths.Manual} element={<Manual/>} handle={{titleKey: PageTitleKeys.Manual}}/>

                <Route element={<PublicOnlyGuard />}>
                    <Route element={<PublicLayout/>}>
                        <Route path={RoutePaths.Login} element={<Login/>} handle={{titleKey: PageTitleKeys.Login}}/>
                        <Route path={RoutePaths.VerifyTwoFactor} element={<VerifyTwoFactorAuth/>}
                               handle={{titleKey: PageTitleKeys.VerifyTwoFactor}}/>
                        <Route element={<RegistrationFeatureGuard/>}>
                            <Route path={RoutePaths.Register} element={<Register/>}
                                   handle={{titleKey: PageTitleKeys.Register}}/>
                        </Route>
                        <Route path={RoutePaths.ThankYouForRegistration} element={<ThankYouForRegistration/>}
                               handle={{titleKey: PageTitleKeys.ThankYouForRegistration}}/>
                        <Route path={RoutePaths.EmailConfirmed} element={<EmailConfirmed/>}
                               handle={{titleKey: PageTitleKeys.EmailConfirmed}}/>
                        <Route path={RoutePaths.RequestPasswordReset} element={<RequestPasswordReset/>}
                               handle={{titleKey: PageTitleKeys.RequestPasswordReset}}/>
                        <Route path={RoutePaths.ChangePassword} element={<ChangePassword/>}
                               handle={{titleKey: PageTitleKeys.ChangePassword}}/>
                        <Route path={RoutePaths.FirstPasswordSetup} element={<FirstPassword/>}
                               handle={{titleKey: PageTitleKeys.FirstPasswordSetup}}/>
                        <Route path={RoutePaths.CheckMailbox} element={<CheckYourMailbox/>}
                               handle={{titleKey: PageTitleKeys.CheckMailbox}}/>
                        <Route path={RoutePaths.AccountActivation} element={<AccountActivation/>}
                               handle={{titleKey: PageTitleKeys.AccountActivation}}/>
                        <Route path={RoutePaths.EntraLogin} element={<EntraCallback/>}
                               handle={{titleKey: PageTitleKeys.EntraLogin}}/>
                    </Route>
                </Route>
            </Route>

            <Route element={<AuthenticationRequiredGuard />}>
                <Route path={RoutePaths.Eula} element={<Eula/>} handle={{titleKey: PageTitleKeys.Eula}}/>

                <Route element={<ConsentRequiredGuard />}>
                    <Route element={<RoleLayout />}>
                        <Route element={<LicenseActivationGuard />}>
                            <Route element={<LicenseRoleGuard allow="admin" />}>
                                <Route path={RoutePaths.LicenseActivation} element={<LicenseActivation/>}
                                       handle={{titleKey: PageTitleKeys.LicenseActivation}}/>
                            </Route>

                            <Route element={<LicenseRoleGuard allow="user" />}>
                                <Route path={RoutePaths.LicensePending} element={<LicenseActivation pending />}
                                       handle={{titleKey: PageTitleKeys.LicensePending}}/>
                            </Route>
                        </Route>

                        <Route element={<LicenseRequiredGuard />}>
                            <Route element={<PrivateLayout/>}>
                                <Route element={<PrivateRoute/>}>

                                    <Route path={RoutePaths.Root} element={<Layout/>} handle={{titleKey: PageTitleKeys.Home}}>
                                        <Route path={RoutePaths.Admin} element={<AdminPanelGuard />}>
                                            <Route path={RoutePaths.AdminGroupList} Component={GroupList}
                                                   handle={{titleKey: PageTitleKeys.AdminGroupList}}/>
                                            <Route path={RoutePaths.AdminGroupDetails} Component={GroupDetails}
                                                   handle={{titleKey: PageTitleKeys.AdminGroupDetails}}/>
                                            <Route path={RoutePaths.AdminUserCreate} element={<CreateUser/>}
                                                   handle={{titleKey: PageTitleKeys.AdminUserCreate}}/>
                                            <Route path={RoutePaths.AdminUserList} element={<UserList/>}
                                                   handle={{titleKey: PageTitleKeys.AdminUserList}}/>
                                            <Route path={RoutePaths.AdminUserEdit} element={<UserEdit/>}
                                                   handle={{titleKey: PageTitleKeys.AdminUserEdit}}/>
                                            <Route path={RoutePaths.AdminDataSourcesList} element={<AdminDataSourceList/>}
                                                   handle={{titleKey: PageTitleKeys.AdminDataSourcesList}}/>
                                            <Route path={RoutePaths.AdminScheduler} Component={DataSourceUpdateScheduler}
                                                   handle={{titleKey: PageTitleKeys.AdminScheduler}}/>
                                            <Route path={RoutePaths.AdminReportedIssues} element={<ReportedIssuesList/>}
                                                   handle={{titleKey: PageTitleKeys.AdminReportedIssues}}/>
                                            <Route path={RoutePaths.AdminPrompts}
                                                   element={<FeatureGuard featureKey={FEATURE_KEYS.DOCUMENT_ANALYSIS_KEY}><Prompts/></FeatureGuard>}
                                                   handle={{titleKey: PageTitleKeys.AdminPrompts}}/>
                                            <Route path={RoutePaths.AdminConfiguration} element={<Configuration/>}
                                                   handle={{titleKey: PageTitleKeys.AdminConfiguration}}/>
                                            <Route path={RoutePaths.AdminAppInfo} element={<AppInfo/>}
                                                   handle={{titleKey: PageTitleKeys.AdminAppInfo}}/>
                                            <Route path={RoutePaths.AdminLogs} element={<Logs/>}
                                                   handle={{titleKey: PageTitleKeys.AdminLogs}}/>
                                            <Route path={RoutePaths.AdminStatistics} element={<Statistics/>}
                                                   handle={{titleKey: PageTitleKeys.AdminStatistics}}/>
                                            <Route path={RoutePaths.AdminLicenseManagement} element={<LicenseManagement/>}
                                                   handle={{titleKey: PageTitleKeys.AdminLicenseManagement}}/>
                                        </Route>
                                        <Route path={RoutePaths.Dashboard}
                                               element={<Dashboard/>}
                                               handle={{titleKey: PageTitleKeys.Dashboard}}/>
                                        <Route path={RoutePaths.Chat}
                                               element={<FeatureGuard featureKey={FEATURE_KEYS.DOCUMENT_ANALYSIS_KEY}><ChatWorkspace/></FeatureGuard>}
                                               handle={{titleKey: PageTitleKeys.Chat}}/>
                                        <Route path={RoutePaths.ChatWithId}
                                               element={<FeatureGuard featureKey={FEATURE_KEYS.DOCUMENT_DIGITIZATION_KEY}><ChatWorkspace/></FeatureGuard>}
                                               handle={{titleKey: PageTitleKeys.ChatWithId}}/>
                                        <Route path={RoutePaths.UserDataSourcesList}
                                               element={<UserDataSourceList/>}
                                               handle={{titleKey: PageTitleKeys.UserDataSourcesList}}/>
                                        <Route path={RoutePaths.DataSourceDocuments}
                                               element={<DataSourceDocumentList/>}
                                               handle={{titleKey: PageTitleKeys.DataSourceDocuments}}/>
                                        <Route path={RoutePaths.DocumentView} Component={FileViewer}
                                               handle={{titleKey: PageTitleKeys.DocumentView}}/>
                                        <Route path={RoutePaths.Payments}
                                               element={<Payments/>}
                                               handle={{titleKey: PageTitleKeys.Payments}}/>
                                        <Route path={RoutePaths.PaymentSuccess}
                                               element={<PaymentSuccess/>}
                                               handle={{titleKey: PageTitleKeys.PaymentSuccess}}/>
                                        <Route path={RoutePaths.PaymentFailure}
                                               element={<PaymentFailure/>}
                                               handle={{titleKey: PageTitleKeys.PaymentFailure}}/>
                                        <Route path={RoutePaths.UserProfile}
                                               element={<UserSettings/>}
                                               handle={{titleKey: PageTitleKeys.UserProfile}}/>
                                        <Route path={RoutePaths.OcrBatch}
                                               element={<FeatureGuard featureKey={FEATURE_KEYS.OCR_KEY}><OcrBatch/></FeatureGuard>}
                                               handle={{titleKey: PageTitleKeys.OcrBatch}}/>
                                        <Route path={RoutePaths.OcrBatchDetails}
                                               element={<FeatureGuard featureKey={FEATURE_KEYS.OCR_KEY}><OcrBatchDetails/></FeatureGuard>}
                                               handle={{titleKey: PageTitleKeys.OcrBatchDetails}}/>
                                        <Route path={RoutePaths.OcrFileResultPreview}
                                               element={<FeatureGuard featureKey={FEATURE_KEYS.OCR_KEY}><OcrResultPreview/></FeatureGuard>}
                                               handle={{titleKey: PageTitleKeys.OcrFileResultPreview}}/>
                                        <Route path={RoutePaths.TextTranslator}
                                               element={<FeatureGuard featureKey={FEATURE_KEYS.TRANSLATION_KEY}><TextTranslatorPage/></FeatureGuard>}
                                               handle={{titleKey: PageTitleKeys.TextTranslator}}/>
                                        <Route path={RoutePaths.DocumentsDigitization}
                                               element={<FeatureGuard featureKey={FEATURE_KEYS.DOCUMENT_DIGITIZATION_KEY}><DocumentsDigitization/></FeatureGuard>}
                                               handle={{titleKey: PageTitleKeys.DocumentsDigitization}}/>
                                        <Route path={RoutePaths.DigitizationDetails}
                                               element={<FeatureGuard featureKey={FEATURE_KEYS.DOCUMENT_DIGITIZATION_KEY}><DigitizationDetails/></FeatureGuard>}
                                               handle={{titleKey: PageTitleKeys.DigitizationDetails}}/>
                                        <Route path={RoutePaths.DigitizationEdition}
                                               element={<FeatureGuard featureKey={FEATURE_KEYS.DOCUMENT_DIGITIZATION_KEY}><DigitizationEdition/></FeatureGuard>}
                                               handle={{titleKey: PageTitleKeys.DigitizationEdition}}/>
                                        <Route index
                                               element={<Navigate to={RoutePaths.Dashboard} replace/>}
                                               handle={{titleKey: PageTitleKeys.Dashboard}}/>
                                    </Route>
                                </Route>
                            </Route>
                        </Route>
                    </Route>
                </Route>
            </Route>

            <Route path={RoutePaths.CatchAll} element={<Login/>} handle={{titleKey: PageTitleKeys.Login}}/>
        </Route>
    ), {
        future: {
            v7_relativeSplatPath: true,
            v7_normalizeFormMethod: true,
            v7_partialHydration: true,
            v7_fetcherPersist: true,
            v7_skipActionErrorRevalidation: true,
        },
    }
);
